/*
  RetroVerse cart drawer polish - added Sep 2026.
  Ports the UpCart layout onto the native theme cart drawer.
  Values below are taken from the UpCart custom stylesheet, not eyeballed.
  Pure presentation. Touches nothing in the cart or checkout data.

  Gotchas worth knowing if you edit this:
  - The upsell snippet prints a per-block <style> with !important at three classes deep.
    Anything competing with it needs an element qualifier to out-specify (see BTN).
  - `height` on the Add button does not take, even inline with !important, because of
    how the flex chain resolves. `min-height` with `height:auto` does.
  - Image-column centring via translateY did not stick; top/bottom + margin:auto does.
  - Price weights are set from JS because several price nodes are styled by rules this
    stylesheet cannot out-specify. Inline + important always wins.
  - The Shipping Protection icon in UpCart is not an image: it is the parcel emoji on a
    dark rounded square. Reproduced the same way here.
  - Titles are no longer clamped, so line one would run under the absolutely positioned
    badge. reserveBadgeSpace() measures each badge and floats a spacer of exactly that
    width into the title, per card. Badge widths differ a lot ("2X THE GAMES" vs
    "TRENDING"), and some cards have no badge, so a fixed value does not work.
  - Sep 14 2026: the fixes run from a MutationObserver callback, synchronously, not on
    the next animation frame. The callback runs after a re-render is in the DOM but
    before the browser paints it, so the server's raw output ("Total: 1 Items", the
    unformatted upsell prices) is never drawn. Queuing onto requestAnimationFrame left
    that to chance, and rAF does not run at all in a tab the browser considers hidden,
    where the raw text was measured staying up for 750ms. The observer sits on the
    <cart-drawer> element itself, which the theme never replaces.
*/
(function () {
  'use strict';

  var BTN = '.cart-drawer .upsell-product-action-column button[class*="upsell-add-button-"]';
  var CARD = '.cart-drawer [class*="upsell-product-card-"]';
  var IMGCOL = '.cart-drawer [class*="upsell-product-image-column-"]';
  var TITLE = '.cart-drawer [class*="upsell-product-title-"]';
  var DESC = '.cart-drawer [class*="upsell-product-description-"]';
  var PRICE = '.cart-drawer [class*="upsell-product-price-"]';
  var BADGE = '.cart-drawer [class*="upsell-best-seller-tag-"]';
  var INK = '#171717';
  var GUARANTEE_BLUE = '#00B8FF';
  var PRICE_RE = /^-?\$[\d,]+(\.\d{2})?$/;

  var CSS = [
    CARD + '{min-height:86px!important;border:1px solid #dedede!important;border-radius:10px!important}',
    IMGCOL + '{top:0!important;bottom:0!important;margin:auto 0!important;transform:none!important;left:10px!important;width:72px!important;min-width:72px!important;max-width:72px!important;height:72px!important;min-height:72px!important;max-height:72px!important;border-radius:7px!important;background:#f7f7f7!important}',
    '.cart-drawer .upsell-product-img{object-fit:contain!important;padding:8px;box-sizing:border-box}',
    TITLE + '{display:block!important;-webkit-line-clamp:unset!important;-webkit-box-orient:unset!important;overflow:visible!important;max-width:100%!important;font-size:13px!important;font-weight:var(--font-weight-bold)!important;line-height:1.25!important;letter-spacing:-0.1px!important;color:' + INK + '!important}',
    TITLE + '::before{content:"";float:right;width:var(--rv-badge-w,0px);height:16px}',
    DESC + '{font-size:10px!important;line-height:1.3!important;color:#666666!important;opacity:1!important}',
    PRICE + '{font-size:12px!important;font-weight:700!important;color:' + INK + '!important;margin-top:5px!important}',
    '.cart-drawer .rv-cap{font-size:10px!important;font-weight:500!important;color:#929292!important;text-decoration:line-through;margin-right:4px}',
    BADGE + '{top:-1px!important;right:12px!important;height:18px!important;min-height:18px!important;padding:0 8px!important;background:#f5f5f5!important;color:' + INK + '!important;border:1px solid #dedede!important;border-top:0!important;border-radius:0 0 4px 4px!important;font-size:9px!important;font-weight:var(--font-weight-bold)!important;line-height:1!important;letter-spacing:0!important;gap:0!important}',
    BTN + '{min-height:29px!important;height:auto!important;min-width:58px!important;line-height:1!important;padding:0 10px!important;font-size:11px!important;font-weight:700!important;border-radius:5px!important;background:' + INK + '!important}',
    '.cart-drawer .upsell-product-action-column{align-items:center!important;height:auto!important;padding-top:0!important;padding-bottom:0!important}',
    '@media (min-width:767px){.cart-drawer .upsell-product-action-column{padding:0 8px 0 0!important;align-items:center!important}}',
    '@media (max-width:767px){.cart-drawer .upsell-product-action-column{top:50%!important;transform:translateY(-50%)!important;padding:0!important}}',

    /* The cart drawer section's inline styles paint a pressed or focused +/- button
       #f5f5f5 with !important, which showed as a grey square on every tap. It cannot
       be keyed off :focus-visible: after each tap the theme re-renders the drawer and
       focuses the new button from script, which the browser flags as focus-visible.
       So the grey is only drawn while html.rv-kbd is set, meaning Tab was the last
       input (see the listeners after this array).
       :not(#rv-np) matches every element and counts as one ID of specificity. It is
       used here and below to outrank the theme's own !important rules. */
    '.cart-drawer quantity-input.quantity.cart-quantity button.quantity__button:not(#rv-np){background-color:transparent!important}',
    'html.rv-kbd .cart-drawer quantity-input.quantity.cart-quantity button.quantity__button:focus:not(#rv-np){background-color:#f5f5f5!important}',

    /* Sep 14 2026: no press effects. Phones paint a highlight box on every tap, and
       they also keep :hover "stuck" on whatever was last tapped. Worse, after each
       cart update the drawer is re-rendered under the finger, so the fresh checkout
       button starts out hovered: its shine sweeps across it, it lifts 2px and its
       shadow deepens, all without anyone touching it. Everything below the tap
       highlight is gated to touch screens, so mouse hover on desktop is unchanged,
       and each value restores the resting look rather than inventing a new one.
       The sticky add-to-cart bar is left out of the lift rule because it rests with
       no shadow at all, and its own stylesheet already cancels its hover lift. */
    'html,html *{-webkit-tap-highlight-color:transparent}',
    'html:not(.rv-kbd) .cart-drawer .gb-shipping-protection-button input:focus+.slider{box-shadow:none!important}',
    '@media (hover:none),(pointer:coarse){' +
      '.button-gradient-effect.gradient-shine-enabled::before{transition:none!important}' +
      '.button-gradient-effect.gradient-shine-enabled:hover::before{left:-100%!important}' +
      '.button-box-shadow.intensity-2:not(.sticky-add-to-cart__button):not(#rv-np):hover,.button-box-shadow.intensity-2:not(.sticky-add-to-cart__button):not(#rv-np):focus,.button-box-shadow.intensity-2:not(.sticky-add-to-cart__button):not(#rv-np):active{transform:none!important;box-shadow:rgba(0,0,0,.15) 0 4px 8px!important}' +
      '.cart-drawer button.cart__checkout-button:hover,.cart-drawer button.cart__checkout-button:focus,.cart-drawer button.cart__checkout-button:active{opacity:1!important;filter:none!important}' +
      BTN + ':hover,' + BTN + ':focus,' + BTN + ':active{opacity:1!important;background:' + INK + '!important}' +
      '.cart-drawer button.cart-remove-button:hover,.cart-drawer button.cart-remove-button:focus,.cart-drawer button.cart-remove-button:active{opacity:1!important}' +
      'a.cart-item__name:hover{text-decoration:none}' +
    '}',

    '.cart-drawer .cart-item__final-price{font-size:14px!important;color:' + INK + '!important}',
    '.cart-drawer .cart-item__old-price{font-size:12px!important;font-weight:500!important;color:#8a8a8a!important;text-decoration:line-through}',

    '.cart-drawer .gb-shipping-protection-icon{width:42px!important;height:42px!important;min-width:42px!important;flex:0 0 42px!important;display:flex!important;align-items:center!important;justify-content:center!important;border-radius:7px!important;background:' + INK + '!important;font-size:21px!important;line-height:1!important;overflow:hidden!important}',
    '.cart-drawer .gb-shipping-protection-icon img{display:none!important}',
    '.cart-drawer .gb-shipping-protection-icon::after{content:"\\1F4E6"}',
    '.cart-drawer .gb-shipping-protection-icontext h2{display:flex;align-items:center;justify-content:space-between;gap:8px;font-size:13px!important;font-weight:700!important;color:' + INK + '!important}',
    '.cart-drawer .gb-shipping-protection-icontext h2 span{margin-left:auto;white-space:nowrap;font-size:12px!important}',
    '.cart-drawer .gb-shipping-protection-icontext p{font-size:10px!important;line-height:1.35!important;color:#666666!important}',

    '.cart-drawer .total_count{font-size:13px!important;font-weight:700!important;color:' + INK + '!important}',
    '.cart-drawer .totals__total-value{font-size:16px!important;color:' + INK + '!important}',
    '.cart-drawer h2.gb-30day-back svg{fill:' + GUARANTEE_BLUE + '!important;color:' + GUARANTEE_BLUE + '!important}'
  ].join('');

  /* Keyboard users keep the +/- focus fill: Tab turns html.rv-kbd on, and any tap or
     click turns it off again. See the +/- note in the CSS above. */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Tab') document.documentElement.classList.add('rv-kbd');
  }, true);
  document.addEventListener('pointerdown', function () {
    document.documentElement.classList.remove('rv-kbd');
  }, true);

  function injectCSS() {
    var s = document.getElementById('rv-cart-match-css');
    if (!s) {
      s = document.createElement('style');
      s.id = 'rv-cart-match-css';
    }
    if (s.textContent !== CSS) s.textContent = CSS;
    if (document.body && s.parentNode !== document.body) document.body.appendChild(s);
    else if (!s.parentNode) (document.head || document.documentElement).appendChild(s);
  }

  function money(cents) {
    if (typeof cents !== 'number') return '';
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function selectedVariant(product, form) {
    if (!product || !product.variants || !product.variants.length) return null;
    var input = form ? form.querySelector('input[name="id"]') : null;
    var id = input ? parseInt(input.value, 10) : 0;
    if (id) {
      for (var i = 0; i < product.variants.length; i++) {
        if (product.variants[i].id === id) return product.variants[i];
      }
    }
    return product.variants[0];
  }

  function comparePrices(root) {
    var cards = root.querySelectorAll('.upsell-product-container');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var priceEl = card.querySelector('[class*="upsell-product-price-"]');
      var jsonEl = card.querySelector('script[id^="ProductJson-"]');
      if (!priceEl || !jsonEl) continue;

      var product;
      try { product = JSON.parse(jsonEl.textContent); } catch (e) { continue; }

      var v = selectedVariant(product, card.querySelector('form'));
      var price = v ? v.price : product.price;
      var cap = v ? v.compare_at_price : product.compare_at_price;

      var html = '';
      if (cap && price && cap > price) html += '<s class="rv-cap">' + money(cap) + '</s>';
      html += '<span class="rv-now">' + money(price) + '</span>';

      if (priceEl.getAttribute('data-rv') !== html) {
        priceEl.innerHTML = html;
        priceEl.setAttribute('data-rv', html);
      }
    }
  }

  /* Float a spacer the exact width of this card's badge into the title, so line one
     wraps before the badge instead of running under it. The float does not change the
     title element's own box, so measuring stays stable across runs. */
  function reserveBadgeSpace(root) {
    var cards = root.querySelectorAll('.upsell-product-container');
    for (var i = 0; i < cards.length; i++) {
      var title = cards[i].querySelector('[class*="upsell-product-title-"]');
      if (!title) continue;
      var badge = cards[i].querySelector('[class*="upsell-best-seller-tag-"]');

      var reserve = 0;
      if (badge) {
        var t = title.getBoundingClientRect();
        var b = badge.getBoundingClientRect();
        if (b.width > 0 && t.width > 0) {
          var overlap = Math.ceil(t.right - b.left);
          if (overlap > 0) reserve = Math.min(overlap + 6, Math.floor(t.width * 0.6));
        }
      }

      var next = reserve + 'px';
      if (title.style.getPropertyValue('--rv-badge-w') !== next) {
        title.style.setProperty('--rv-badge-w', next);
      }
    }
  }

  function boldPrices(root) {
    var nodes = root.querySelectorAll('span, div, p, strong, b, h2, h3, td');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.children.length) continue;
      var txt = (el.textContent || '').trim();
      if (!PRICE_RE.test(txt)) continue;
      if (el.getAttribute('data-rv-bold') === '1') continue;

      var cls = String(el.className || '');
      if (cls.indexOf('old-price') > -1 || cls.indexOf('rv-cap') > -1) continue;
      if (el.closest('s, del, .rv-cap, [class*="old-price"], [class*="compare"]')) continue;
      var deco = '';
      try { deco = getComputedStyle(el).textDecorationLine || ''; } catch (e) {}
      if (deco.indexOf('line-through') > -1) continue;

      el.style.setProperty('font-weight', '700', 'important');
      el.setAttribute('data-rv-bold', '1');
    }
  }

  function stripBadgeIcons(root) {
    var icons = root.querySelectorAll('.bestseller-icon');
    for (var i = icons.length - 1; i >= 0; i--) {
      if (icons[i].parentNode) icons[i].parentNode.removeChild(icons[i]);
    }
  }

  function fixTotalPlural(root) {
    var els = root.querySelectorAll('.total_count');
    for (var i = 0; i < els.length; i++) {
      var txt = els[i].textContent;
      var m = txt.match(/(\d+)\s*Items?\b/i);
      if (!m) continue;
      var n = parseInt(m[1], 10);
      var next = txt.replace(/(\d+)\s*Items?\b/i, m[1] + ' ' + (n === 1 ? 'Item' : 'Items'));
      if (next !== txt) els[i].textContent = next;
    }
  }

  /* Sep 15 2026: the theme redraws the cart by replacing the whole drawer's HTML, so
     every product image becomes a brand-new <img>. iPhone Safari paints a new <img>
     blank for a frame while it decodes, even straight from cache. At 60fps in the
     user's recording, every image blinked out on an add or a quantity change, which
     read as the cart "spazzing". run() is called from the MutationObserver before the
     browser paints, so here each new <img> is swapped for the already-decoded element
     that showed the same picture before the redraw. The new element's attributes are
     copied onto the old one first, so whatever the server sent still wins. */
  var decodedImgs = {};

  function copyAttributes(from, to) {
    var i, a;
    for (i = to.attributes.length - 1; i >= 0; i--) {
      a = to.attributes[i].name;
      if (!from.hasAttribute(a)) to.removeAttribute(a);
    }
    for (i = 0; i < from.attributes.length; i++) {
      a = from.attributes[i];
      if (to.getAttribute(a.name) !== a.value) to.setAttribute(a.name, a.value);
    }
  }

  function keepDecodedImages(root) {
    var imgs = root.querySelectorAll('img');
    var next = {};
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      var key = img.getAttribute('src');
      if (!key) continue;
      var pool = decodedImgs[key] || [];
      for (var j = 0; j < pool.length; j++) {
        var old = pool[j];
        if (old === img || old.isConnected || !old.complete || !old.naturalWidth) continue;
        copyAttributes(img, old);
        img.replaceWith(old);
        pool.splice(j, 1);
        img = old;
        break;
      }
      (next[key] = next[key] || []).push(img);
    }
    decodedImgs = next;
  }

  function drawer() {
    return document.querySelector('.cart-drawer') ||
           document.querySelector('#CartDrawer') ||
           document.querySelector('cart-drawer');
  }

  function run() {
    injectCSS();
    var root = drawer() || document;
    keepDecodedImages(root);
    comparePrices(root);
    stripBadgeIcons(root);
    fixTotalPlural(root);
    boldPrices(root);
    reserveBadgeSpace(root);
  }

  function start() {
    run();

    /* Synchronous on purpose - see the note at the top. Every step above only
       writes when something actually differs, so the mutations run() makes itself
       settle after one extra pass instead of looping. */
    var host = document.querySelector('cart-drawer') || drawer();
    if (host && window.MutationObserver) {
      new MutationObserver(run).observe(host, { childList: true, subtree: true });
    }

    window.addEventListener('resize', function () { reserveBadgeSpace(drawer() || document); });

    document.addEventListener('change', function (e) {
      var cls = e.target && e.target.className ? String(e.target.className) : '';
      if (cls.indexOf('upsell-variant-select') > -1) setTimeout(run, 80);
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
